// src/data/simulations.js - VERSION ENRICHIE AVEC TYPE ET JUSTIFICATION

/**
 * Ce fichier contient les données des entreprises utilisées pour simuler les opérations
 * boursières affichées dans les cartes d'investissement.
 * Chaque objet est enrichi d'un 'type' (Achat/Vente/Maintien) et d'une 'justification'
 * pour rendre la simulation plus réaliste.
 */

// Fonctions utilitaires pour obtenir des choix aléatoires
const getRandomElement = (array) => array[Math.floor(Math.random() * array.length)];
const getRandomType = () => getRandomElement(['Achat', 'Vente', 'Maintien']);

// Définitions des justifications par secteur pour une meilleure cohérence
const justifications = {
    "Industrie": [
        "Augmentation des commandes après l'annonce d'un nouveau contrat d'infrastructure.",
        "Ralentissement de la production suite à des problèmes de chaîne d'approvisionnement.",
        "Stabilité du carnet de commandes, pas de changement majeur dans la demande.",
        "Innovation technologique ouvrant de nouveaux marchés industriels."
    ],
    "Télécommunications": [
        "Lancement d'une nouvelle offre 5G prometteuse en zone urbaine.",
        "Forte concurrence sur les prix affectant la marge bénéficiaire.",
        "Réglementations favorables à l'expansion des infrastructures réseau.",
        "Forte croissance du nombre d'abonnés aux services de données."
    ],
    "Finance": [
        "Bénéfices records grâce à l'augmentation des taux d'intérêt.",
        "Augmentation des provisions pour risques de crédit en période économique incertaine.",
        "Acquisition stratégique d'une Fintech pour moderniser les services.",
        "Résultats stables, conformes aux attentes du marché."
    ],
    "Énergie": [
        "Volatilité des prix du baril de pétrole, forte incertitude.",
        "Investissement majeur dans les énergies renouvelables (solaire/éolien).",
        "Décision politique favorable à l'exploitation de nouvelles ressources.",
        "Maintien de la demande d'énergie malgré les efforts de transition."
    ],
    "Technologie": [
        "Résultats trimestriels supérieurs aux prévisions grâce à l'IA.",
        "Mauvaise réception d'un nouveau produit phare par le marché.",
        "Forte demande pour les semi-conducteurs et les services cloud.",
        "Enquête antitrust menaçant le modèle économique de l'entreprise."
    ],
    "Matières premières": [
        "Hausse des prix du minerai de fer/cuivre due à la reprise de l'industrie chinoise.",
        "Grève dans une mine clé réduisant la production et l'offre mondiale.",
        "Demande minière stable, en ligne avec les projections annuelles.",
        "Nouvelle réglementation environnementale augmentant les coûts de production."
    ],
    "Biens de consommation": [
        "Succès d'une nouvelle campagne de marketing ciblant la génération Z.",
        "Inflation des coûts des matières premières et des transports.",
        "Fidélité client élevée assurant un flux de revenus constant.",
        "Changement des préférences des consommateurs vers des marques locales."
    ],
    "Alimentation": [
        "Conditions météorologiques favorables augmentant le rendement agricole.",
        "Rappel de produits coûteux affectant la réputation de l'entreprise.",
        "Lancement de produits biologiques et sains avec une forte marge.",
        "Pressions des distributeurs pour baisser les prix."
    ],
    "Automobile": [
        "Forte progression des ventes de véhicules électriques.",
        "Pénurie de puces électroniques limitant la capacité de production.",
        "Partenariat stratégique pour le développement de la conduite autonome.",
        "Recul des ventes sur le marché clé de l'Asie."
    ],
    "Commerce électronique": [
        "Forte croissance du trafic et des ventes pendant les fêtes.",
        "Augmentation des coûts de livraison et de logistique.",
        "Expansion réussie dans une nouvelle région géographique.",
        "Nouvelles lois sur la protection des données affectant la publicité ciblée."
    ],
    "Commerce de détail": [
        "Ouverture réussie de plusieurs nouveaux magasins phares.",
        "Fermetures de magasins dues à la concurrence en ligne.",
        "Augmentation des ventes grâce à des promotions agressives.",
        "Inventaire bien géré, évitant les surstocks."
    ],
    "Santé": [
        "Résultats prometteurs d'un essai clinique de phase 3.",
        "Retard d'approbation d'un médicament clé par l'agence de réglementation.",
        "Contrat gouvernemental important pour la fourniture de vaccins/matériels.",
        "Forte pression sur les prix des génériques."
    ],
    "Divertissement": [
        "Succès planétaire d'une nouvelle série ou d'un film sur la plateforme.",
        "Baisse du nombre d'abonnés au service de streaming.",
        "Acquisition de droits sportifs exclusifs augmentant l'attractivité.",
        "Augmentation des coûts de production de contenu."
    ],
    "Boissons": [
        "Lancement d'une boisson innovante très populaire auprès des jeunes.",
        "Problèmes d'approvisionnement en eau ou en matières premières.",
        "Croissance stable dans les marchés émergents.",
        "Taxe gouvernementale sur le sucre impactant les prix."
    ],
    "Conglomérat": [
        "Excellents résultats dans la division Assurance/Énergie, tirant l'ensemble du groupe.",
        "Cession d'une division non rentable pour se concentrer sur le cœur de métier.",
        "Stratégie d'investissement à long terme, maintien de position.",
        "Impact négatif des fluctuations monétaires sur les résultats internationaux."
    ]
};

// Fonction pour générer une simulation aléatoire cohérente pour une entreprise donnée
const getCoherentSimulation = (sector) => {
    const type = getRandomType();
    let justificationList = justifications[sector] || justifications["Finance"]; // Fallback à Finance
    
    // Tentative d'aligner le type avec la justification (pour plus de cohérence)
    let finalJustification = getRandomElement(justificationList);

    if (type === 'Achat') {
        // Favoriser les justifications positives
        finalJustification = getRandomElement(justificationList.filter(j => 
            j.includes('Augmentation') || j.includes('Succès') || j.includes('résultats prometteurs') || j.includes('Forte progression') || j.includes('Lancement') || j.includes('Croissance') || j.includes('Hausse') || j.includes('Innovation')
        ) || justificationList);
    } else if (type === 'Vente') {
        // Favoriser les justifications négatives
        finalJustification = getRandomElement(justificationList.filter(j => 
            j.includes('Ralentissement') || j.includes('Mauvaise réception') || j.includes('Ralentissement') || j.includes('Baisse') || j.includes('Retard') || j.includes('Enquête') || j.includes('Problèmes') || j.includes('Pénurie') || j.includes('fermetures')
        ) || justificationList);
    } 
    // Si 'Maintien', toute justification est acceptable.

    return { type, justification: finalJustification };
};


export const investmentSimulations = [
  // Afrique de l'Ouest et du Centre
  { company: "Dangote Cement", symbol: "DANGCEM.LG", sector: "Industrie", ...getCoherentSimulation("Industrie") },
  { company: "MTN Nigeria", symbol: "MTNN.LG", sector: "Télécommunications", ...getCoherentSimulation("Télécommunications") },
  { company: "Ecobank", symbol: "ETI.LG", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Sonatrach", symbol: "SON.AG", sector: "Énergie", ...getCoherentSimulation("Énergie") },
  { company: "Maroc Telecom", symbol: "IAM.CS", sector: "Télécommunications", ...getCoherentSimulation("Télécommunications") },
  { company: "Attijariwafa Bank", symbol: "AWB.CS", sector: "Finance", ...getCoherentSimulation("Finance") },
  
  // Afrique du Sud
  { company: "Naspers", symbol: "NPN.J", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Sasol", symbol: "SOL.J", sector: "Énergie", ...getCoherentSimulation("Énergie") },
  { company: "Standard Bank", symbol: "SBK.J", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Anglo American", symbol: "AAL.L", sector: "Matières premières", ...getCoherentSimulation("Matières premières") },
  { company: "Old Mutual", symbol: "OMU.J", sector: "Finance", ...getCoherentSimulation("Finance") },

  // Europe
  { company: "LVMH", symbol: "MC.PA", sector: "Biens de consommation", ...getCoherentSimulation("Biens de consommation") },
  { company: "Nestlé", symbol: "NESN.VX", sector: "Alimentation", ...getCoherentSimulation("Alimentation") },
  { company: "ASML Holding", symbol: "ASML.AS", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "SAP", symbol: "SAP.DE", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Siemens", symbol: "SIE.DE", sector: "Industrie", ...getCoherentSimulation("Industrie") },
  { company: "TotalEnergies", symbol: "TTE.PA", sector: "Énergie", ...getCoherentSimulation("Énergie") },
  { company: "Roche Holding", symbol: "ROG.VX", sector: "Santé", ...getCoherentSimulation("Santé") },
  { company: "Sanofi", symbol: "SAN.PA", sector: "Santé", ...getCoherentSimulation("Santé") },
  { company: "Volkswagen", symbol: "VOW.DE", sector: "Automobile", ...getCoherentSimulation("Automobile") },
  { company: "Unilever", symbol: "ULVR.L", sector: "Biens de consommation", ...getCoherentSimulation("Biens de consommation") },
  
  // Amérique du Nord (États-Unis et Canada)
  { company: "Apple", symbol: "AAPL", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Microsoft", symbol: "MSFT", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Alphabet (Google)", symbol: "GOOGL", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Amazon", symbol: "AMZN", sector: "Commerce électronique", ...getCoherentSimulation("Commerce électronique") },
  { company: "NVIDIA", symbol: "NVDA", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Tesla", symbol: "TSLA", sector: "Automobile", ...getCoherentSimulation("Automobile") },
  { company: "Meta Platforms (Facebook)", symbol: "META", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "JPMorgan Chase", symbol: "JPM", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Johnson & Johnson", symbol: "JNJ", sector: "Santé", ...getCoherentSimulation("Santé") },
  { company: "Visa", symbol: "V", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Walmart", symbol: "WMT", sector: "Commerce de détail", ...getCoherentSimulation("Commerce de détail") },
  { company: "Exxon Mobil", symbol: "XOM", sector: "Énergie", ...getCoherentSimulation("Énergie") },
  { company: "Coca-Cola", symbol: "KO", sector: "Boissons", ...getCoherentSimulation("Boissons") },
  { company: "Intel", symbol: "INTC", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Bank of America", symbol: "BAC", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Berkshire Hathaway", symbol: "BRK.A", sector: "Conglomérat", ...getCoherentSimulation("Conglomérat") },
  { company: "Netflix", symbol: "NFLX", sector: "Divertissement", ...getCoherentSimulation("Divertissement") },
  { company: "Procter & Gamble", symbol: "PG", sector: "Biens de consommation", ...getCoherentSimulation("Biens de consommation") },
  { company: "Walt Disney", symbol: "DIS", sector: "Divertissement", ...getCoherentSimulation("Divertissement") },
  { company: "Home Depot", symbol: "HD", sector: "Commerce de détail", ...getCoherentSimulation("Commerce de détail") },
  { company: "PepsiCo", symbol: "PEP", sector: "Alimentation et Boissons", ...getCoherentSimulation("Alimentation") },
  { company: "Salesforce", symbol: "CRM", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Pfizer", symbol: "PFE", sector: "Santé", ...getCoherentSimulation("Santé") },
  
  // Asie
  { company: "Alibaba", symbol: "BABA", sector: "Commerce électronique", ...getCoherentSimulation("Commerce électronique") },
  { company: "Tencent Holdings", symbol: "TCEHY", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Samsung Electronics", symbol: "005930.KS", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Toyota Motor", symbol: "7203.T", sector: "Automobile", ...getCoherentSimulation("Automobile") },
  { company: "Sony", symbol: "6758.T", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "Taiwan Semiconductor", symbol: "TSM", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "LG Electronics", symbol: "066570.KS", sector: "Technologie", ...getCoherentSimulation("Technologie") },
  { company: "HDFC Bank", symbol: "HDFCBANK.NS", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Tata Consultancy", symbol: "TCS.NS", sector: "Technologie", ...getCoherentSimulation("Technologie") },

  // Australie
  { company: "BHP Group", symbol: "BHP.AX", sector: "Matières premières", ...getCoherentSimulation("Matières premières") },
  { company: "Commonwealth Bank", symbol: "CBA.AX", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Rio Tinto", symbol: "RIO.AX", sector: "Matières premières", ...getCoherentSimulation("Matières premières") },
  { company: "Telstra", symbol: "TLS.AX", sector: "Télécommunications", ...getCoherentSimulation("Télécommunications") },
  { company: "Woolworths", symbol: "WOW.AX", sector: "Commerce de détail", ...getCoherentSimulation("Commerce de détail") },

  // Amérique du Sud
  { company: "Petrobras", symbol: "PBR", sector: "Énergie", ...getCoherentSimulation("Énergie") },
  { company: "Vale", symbol: "VALE", sector: "Matières premières", ...getCoherentSimulation("Matières premières") },
  { company: "Banco Bradesco", symbol: "BBD", sector: "Finance", ...getCoherentSimulation("Finance") },
  { company: "Ambev", symbol: "ABEV", sector: "Boissons", ...getCoherentSimulation("Boissons") },
  { company: "MercadoLibre", symbol: "MELI", sector: "Commerce électronique", ...getCoherentSimulation("Commerce électronique") },
];